import { Trash2 } from "lucide-react";
import { TextField } from "@/components/forms";
import { cn } from "@/utils";
import type { EducationEntry } from "./types";
import { APPLICATION_LIMITS, type EducationField, type FieldErrors } from "./validation";

interface EducationCardProps {
  index: number;
  item: EducationEntry;
  /** True while the entry has any problem, shown or not yet shown. */
  incomplete: boolean;
  /** Messages to show now, per field (see `visibleErrors`). */
  errors?: FieldErrors<EducationField>;
  onUpdate: (field: keyof EducationEntry, value: string) => void;
  onRemove: () => void;
}

/** A single editable education entry rendered as a numbered glass card. */
export function EducationCard({
  index,
  item,
  incomplete,
  errors = {},
  onUpdate,
  onRemove,
}: EducationCardProps) {
  return (
    <div
      className={cn(
        "relative rounded-2xl border p-5",
        incomplete && "border-destructive/40",
      )}
      style={{
        background: "var(--landing-surface)",
        borderColor: incomplete ? undefined : "var(--landing-glass-border)",
      }}
    >
      <div className="mb-4 flex items-center justify-between">
        <span
          className="flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold"
          style={{
            background: "rgba(var(--acc2-rgb),0.12)",
            color: "var(--acc2)",
            border: "1px solid rgba(var(--acc2-rgb),0.22)",
          }}
        >
          {index + 1}
        </span>
        <button
          type="button"
          onClick={onRemove}
          className="rounded-lg p-1.5 transition-colors hover:bg-destructive/10 hover:text-destructive"
          style={{ color: "var(--landing-text-dim)" }}
          aria-label="Remove education"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TextField
          label="Institution"
          required
          value={item.institution}
          onChange={(v) => onUpdate("institution", v)}
          error={errors.institution}
          placeholder="University / school"
        />
        <TextField
          label="Degree"
          value={item.degree}
          onChange={(v) => onUpdate("degree", v)}
          error={errors.degree}
          placeholder="Bachelor's, Master's…"
        />
        <TextField
          label="Field of study"
          required
          value={item.fieldOfStudy}
          onChange={(v) => onUpdate("fieldOfStudy", v)}
          error={errors.fieldOfStudy}
          placeholder="Computer Science"
        />
        <TextField
          label="End year"
          required
          type="number"
          min={APPLICATION_LIMITS.YEAR_MIN}
          max={APPLICATION_LIMITS.YEAR_MAX}
          inputMode="numeric"
          value={item.endYear}
          onChange={(v) => onUpdate("endYear", v)}
          error={errors.endYear}
          placeholder="2021"
        />
      </div>
    </div>
  );
}
